/* START_MODULE_CONTRACT
 * PURPOSE: Основной layout приватной зоны: AppBar, сворачиваемая боковая навигация, mobile drawer.
 * SCOPE: Shell authenticated UI — header, sidebar, outlet, footer; публикация navCollapsed (UC-018).
 * DEPENDS: M-FE-AUTH, M-FE-PROJECTS
 * LINKS: M-FE-AUTH, M-FE-LAYOUT, UC-018
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * PrivateLayout - layout приватной зоны с collapsible sidebar.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-08-10 - UC-022: Feature Toggles nav for ADMIN.
 * LAST_CHANGE: 2026-07-28 - UC-018: SidebarLayoutProvider публикует navCollapsed.
 * END_CHANGE_SUMMARY
 */

import { useCallback, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import TuneIcon from '@mui/icons-material/Tune';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { ProjectsProvider } from '../context/ProjectsContext';
import { SidebarLayoutProvider } from '../context/SidebarLayoutContext';
import ProjectsNav from './components/ProjectsNav';
import { shellColors, sidebarNavItemSx, drawerPaperSx, createDrawerWidthTransition, createInteractiveSurfaceTransition } from '../theme/appTheme';

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_MINI = 72;
const SIDEBAR_COLLAPSED_KEY = 'teamplanner:sidebar-collapsed';

const miniItemSx = {
  justifyContent: 'center',
  px: 1.5,
  minHeight: 48,
};

const miniIconSx = {
  minWidth: 0,
  justifyContent: 'center',
};

function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/* START_METHOD_PrivateLayout */
/* START_CONTRACT: PrivateLayout
 * PURPOSE: Основной layout приватной зоны: AppBar, боковая навигация (full/mini), drawer mobile, logout.
 * INPUTS: нет (рендерит Outlet внутри ProjectsProvider).
 * OUTPUTS: { JSX.Element }.
 * SIDE_EFFECTS: Навигация, logout, persist sidebar collapsed в localStorage.
 * LINKS: M-FE-AUTH, M-FE-PROJECTS
 * END_CONTRACT: PrivateLayout
 */
export default function PrivateLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const drawerWidth = sidebarCollapsed && isDesktop ? DRAWER_WIDTH_MINI : DRAWER_WIDTH;
  const navCollapsed = sidebarCollapsed && isDesktop;
  const userInitial = user?.email?.[0]?.toUpperCase() ?? '?';

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const closeMobileDrawer = () => {
    setMobileOpen(false);
  };

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      /* START_BLOCK_SIDEBAR_COLLAPSE_TOGGLE */
      if (next) {
        console.info('[PrivateLayout][toggleSidebarCollapsed][BLOCK_SIDEBAR_COLLAPSED] desktop sidebar collapsed');
      } else {
        console.info('[PrivateLayout][toggleSidebarCollapsed][BLOCK_SIDEBAR_EXPANDED] desktop sidebar expanded');
      }
      /* END_BLOCK_SIDEBAR_COLLAPSE_TOGGLE */
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const handleNavigate = (path) => {
    navigate(path);
    closeMobileDrawer();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderNavItem = (collapsed, { path, icon, label, selected }) => {
    const button = (
      <ListItemButton
        selected={selected}
        onClick={() => handleNavigate(path)}
        sx={{
          ...sidebarNavItemSx(selected),
          ...(collapsed ? miniItemSx : {}),
        }}
      >
        <ListItemIcon sx={collapsed ? miniIconSx : { minWidth: 40 }}>
          {icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{ variant: 'body2', fontWeight: selected ? 600 : 400 }}
          />
        )}
      </ListItemButton>
    );

    if (collapsed) {
      return (
        <Tooltip key={path} title={label} placement="right" arrow>
          {button}
        </Tooltip>
      );
    }

    return button;
  };

  const renderDrawer = (collapsed) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar
        sx={{
          minHeight: { xs: 56, sm: 64 },
          px: collapsed ? 1 : 2,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        {!collapsed && (
          <Typography variant="subtitle1" fontWeight={700} color="primary.main" noWrap>
            TeamPlanner
          </Typography>
        )}
        {collapsed && (
          <Typography variant="subtitle1" fontWeight={700} color="primary.main">
            TP
          </Typography>
        )}
      </Toolbar>

      <Divider sx={{ borderColor: shellColors.sidebarBorder }} />

      <List sx={{ flex: 1, py: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <ProjectsNav onNavigate={closeMobileDrawer} collapsed={collapsed} />

        {renderNavItem(collapsed, {
          path: '/user/info',
          icon: <PersonIcon fontSize="small" />,
          label: 'Профиль',
          selected: location.pathname === '/user/info',
        })}

        {user?.role === 'ADMIN' && (
          <>
            <Divider sx={{ my: 1, mx: 2, borderColor: shellColors.sidebarBorder }} />
            {renderNavItem(collapsed, {
              path: '/admin/users',
              icon: <AdminPanelSettingsIcon fontSize="small" />,
              label: 'Пользователи',
              selected: location.pathname === '/admin/users',
            })}
            {renderNavItem(collapsed, {
              path: '/admin/feature-toggles',
              icon: <TuneIcon fontSize="small" />,
              label: 'Feature Toggles',
              selected: location.pathname === '/admin/feature-toggles',
            })}
          </>
        )}
      </List>

      {isDesktop && (
        <>
          <Divider sx={{ borderColor: shellColors.sidebarBorder }} />
          <Box sx={{ px: 1, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title={collapsed ? 'Развернуть меню' : 'Свернуть меню'} placement="right" arrow>
              <IconButton
                onClick={toggleSidebarCollapsed}
                size="small"
                aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
                sx={{
                  border: 1,
                  borderColor: shellColors.sidebarBorder,
                  borderRadius: 0.5,
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: shellColors.sidebarHover,
                    color: 'text.primary',
                  },
                }}
              >
                {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <ProjectsProvider>
      <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column', bgcolor: 'background.default' }}>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: shellColors.headerBg,
            color: 'text.primary',
            borderBottom: 1,
            borderColor: shellColors.headerBorder,
            backgroundImage: `linear-gradient(180deg, #f8fafb 0%, ${shellColors.headerBg} 100%)`,
          }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              aria-label="Открыть меню"
              sx={{ display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            <Typography
              variant="h6"
              noWrap
              component="button"
              type="button"
              onClick={() => navigate('/projects')}
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.02em',
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
                color: 'inherit',
                p: 0,
                font: 'inherit',
                '&:hover': { color: 'primary.main' },
              }}
            >
              TeamPlanner
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            {user?.email && (
              <Box
                component="button"
                type="button"
                onClick={() => navigate('/user/info')}
                aria-label="Профиль"
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 1,
                  mr: 1,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: shellColors.headerBorder,
                  borderStyle: 'solid',
                  cursor: 'pointer',
                  font: 'inherit',
                  color: 'inherit',
                  transition: createInteractiveSurfaceTransition(theme),
                  '&:hover': { bgcolor: '#ffffff', borderColor: 'rgba(37, 99, 168, 0.25)' },
                }}
              >
                <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: 'primary.main' }}>
                  {userInitial}
                </Avatar>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
                  {user.email}
                </Typography>
              </Box>
            )}

            <Tooltip title="Выйти">
              <IconButton
                color="inherit"
                onClick={handleLogout}
                aria-label="Выйти"
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>

            <Button
              color="inherit"
              onClick={handleLogout}
              startIcon={<LogoutIcon fontSize="small" />}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                textTransform: 'none',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box component="span" sx={{ display: { sm: 'none', md: 'inline' } }}>
                Выйти
              </Box>
            </Button>
          </Toolbar>
        </AppBar>

        <Box sx={{ display: 'flex', flex: 1 }}>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
                ...drawerPaperSx,
              },
            }}
          >
            {renderDrawer(false)}
          </Drawer>

          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              width: drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                transition: createDrawerWidthTransition(theme),
                overflowX: 'hidden',
                ...drawerPaperSx,
              },
            }}
            open
          >
            {renderDrawer(navCollapsed)}
          </Drawer>

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              p: { xs: 2, sm: 3 },
              minWidth: 0,
              width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
              transition: (t) => createDrawerWidthTransition(t),
            }}
          >
            <Toolbar />
            <Box
              sx={{
                flex: 1,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                p: { xs: 2, sm: 3 },
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}
            >
              <SidebarLayoutProvider navCollapsed={navCollapsed}>
                <Outlet />
              </SidebarLayoutProvider>
            </Box>
            <Box
              component="footer"
              sx={{
                py: 2,
                textAlign: 'center',
                mt: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                TeamPlanner &copy; {new Date().getFullYear()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </ProjectsProvider>
  );
}
/* END_METHOD_PrivateLayout */
